/** @jsxImportSource @emotion/react */

import {
  useSession,
  formatErrorResponse,
} from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Input,
  Typography,
  useTheme,
  useForm,
  UtilityButton,
  Snackbar,
  ToggleButtonGroup,
  Switch,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useAsyncFn, useMount, useUpdateEffect } from 'react-use';
import { QBLoader } from 'components';
import { useOrganisation } from 'contexts';
import { getConfig, updateConfig, MerchantConfig, Status } from '../api';
import {
  getConfigAsPlatform,
  updateConfigAsPlatform,
  updatePlatformConfig,
  getPlatformConfig,
} from './api';
import { PlatformForm } from './PlatformConfigForm';
import { Platform } from './types';
import { platformValidators } from './utils/platformValidators';

const defaultConfig: MerchantConfig = {
  config: {
    amountToleranceLowerBound: 100,
    amountToleranceUpperBound: 100,
    minutesToCancelOrderAfter: 0,
    cancelOrderOnError: false,
    retryOrderCallbacks: true,
    useWalletRecipientDuality: false,
    layoutMode: 'ORDERS',
  },
  status: 'ACTIVE',
  displayName: '',
};

const defaultPlatformForm: Platform = {
  defaultMinutesToCancelOrderAfter: 0,
  defaultCancelOrderOnError: false,
  defaultRetryOrderCallbacks: true,
};

const getCancellationComment = (
  minutes: number,
  cancelOrderOnError: boolean
) => {
  if (minutes === 0 && !cancelOrderOnError) {
    return "Orders won't be cancelled.";
  }
  if (minutes === 0 && cancelOrderOnError) {
    return 'Orders will be cancelled when receiving an error.';
  }
  if (minutes > 0 && !cancelOrderOnError) {
    return `Orders will be cancelled after ${minutes} minute${
      minutes !== 1 ? 's' : ''
    } if not completed.`;
  }
  return `Orders will be cancelled after ${minutes} minute${
    minutes !== 1 ? 's' : ''
  } if not completed, or when receiving an error.`;
};

export const ConfigPage = () => {
  const { spacing } = useTheme();
  const organisation = useOrganisation();
  const {
    isPlatform,
    selectedMerchant: { merchantId },
  } = organisation;

  return (
    <>
      {isPlatform && (
        <>
          <PlatformConfig />
          <div css={{ display: 'grid', gap: spacing.xxs }}>
            <Card>
              <Typography variant="heading2">Merchant Config</Typography>
            </Card>
          </div>
        </>
      )}
      <MerchantConfigForm isPlatform={isPlatform} merchantId={merchantId} />
    </>
  );
};

const PlatformConfig = () => {
  const { spacing } = useTheme();
  const {
    fields: platformForm,
    setters: platformFormSetters,
    errors: platformFormErrors,
    edited: platformFormEdited,
  } = useForm(defaultPlatformForm, platformValidators);

  const [message, setMessage] = useState<{
    message: string;
    type: 'success' | 'danger';
  }>();

  const [
    { loading: updatePlatformConfigLoading, error: updatePlatformConfigError },
    updatePlatformConfiguration,
  ] = useAsyncFn(async () => {
    const data = await updatePlatformConfig(platformForm);
    platformFormSetters.set(data);
    setMessage({ message: 'Config saved successfully!', type: 'success' });
  }, [platformForm]);

  const [
    { loading: getPlatformConfigLoading, error: getPlatformConfigError },
    fetchPlatformConfig,
  ] = useAsyncFn(async () => {
    const data = await getPlatformConfig();
    platformFormSetters.set(data);
  }, [platformForm]);

  useMount(() => {
    void fetchPlatformConfig();
  });

  useUpdateEffect(() => {
    if (updatePlatformConfigError) {
      setMessage({
        message: formatErrorResponse(updatePlatformConfigError),
        type: 'danger',
      });
    }
  }, [updatePlatformConfigError]);

  useUpdateEffect(() => {
    if (getPlatformConfigError) {
      setMessage({
        message: formatErrorResponse(getPlatformConfigError),
        type: 'danger',
      });
    }
  }, [getPlatformConfigError]);

  if (getPlatformConfigLoading) return <QBLoader />;
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xxs,
        marginBottom: spacing.xxs,
      }}
    >
      <Card>
        <Typography variant="heading2">Platform Config</Typography>
        <Typography variant="body">
          These default values will be used for all new merchants added to the
          platform
        </Typography>
      </Card>
      <div css={{ maxWidth: '50%' }}>
        <PlatformForm
          formFields={platformForm}
          formErrors={platformFormErrors}
          formSetters={platformFormSetters}
          editable={true}
        />
      </div>
      <UtilityButton
        text="Save platform"
        loading={updatePlatformConfigLoading}
        disabled={!platformFormEdited}
        onClick={() => updatePlatformConfiguration()}
      />
      {message && (
        <Snackbar
          message={message.message}
          variant={message.type}
          open={true}
          onClose={() => {
            setMessage(undefined);
          }}
        />
      )}
    </div>
  );
};

const MerchantConfigForm = ({
  isPlatform,
  merchantId,
}: {
  isPlatform: boolean;
  merchantId: string;
}) => {
  const session = useSession();
  const { spacing, palette } = useTheme();
  const [message, setMessage] = useState<{
    message: string;
    type: 'success' | 'danger';
  }>();
  const [isRecipientDualityDisabled, setIsRecipientDualityDisabled] =
    useState(false);
  const { edited, errors, fields, hasErrors, setters } =
    useForm<MerchantConfig>(defaultConfig, {
      config: {
        amountToleranceLowerBound: (data: number) =>
          data > 100 || data < 0
            ? 'Lower bound has to be between 0 and 100%.'
            : undefined,
        amountToleranceUpperBound: (data: number) =>
          data < 100 ? 'Upper bound has to be 100% or more.' : undefined,
        minutesToCancelOrderAfter: (data: number) =>
          data < 0 ? 'Needs to be positive.' : undefined,
      },
    });

  const [{ error, loading }, update] = useAsyncFn(async () => {
    let conf: MerchantConfig;
    if (isPlatform) {
      conf = await updateConfigAsPlatform(merchantId, fields);
      setters.set(conf);
    } else {
      conf = await updateConfig(fields);
      setters.set(conf);
    }
    setIsRecipientDualityDisabled(conf.config.useWalletRecipientDuality);
    setMessage({ message: 'Config saved successfully!', type: 'success' });
    return conf;
  }, [fields]);

  const [
    { error: fetchConfigError, loading: fetchConfigLoading },
    fetchConfig,
  ] = useAsyncFn(async (merchantId?: string) => {
    let conf: MerchantConfig;
    if (merchantId) {
      conf = await getConfigAsPlatform(merchantId);
      setters.set(conf);
    } else {
      conf = await getConfig();
      setters.set(conf);
    }
    setIsRecipientDualityDisabled(conf.config.useWalletRecipientDuality);
    return conf;
  });

  useEffect(() => {
    if (isPlatform && merchantId) {
      void fetchConfig(merchantId);
    } else {
      void fetchConfig();
    }
  }, [merchantId, isPlatform, fetchConfig]);

  useUpdateEffect(() => {
    if (error) {
      setMessage({ message: formatErrorResponse(error), type: 'danger' });
    }
  }, [error]);

  useUpdateEffect(() => {
    if (fetchConfigError) {
      setMessage({
        message: formatErrorResponse(fetchConfigError),
        type: 'danger',
      });
    }
  }, [fetchConfigError]);

  const canEditCorporateWallet =
    session.authenticated &&
    session.permissions.includes('edit_corporate_wallet');

  return (
    <div>
      <div css={{ marginTop: spacing.xxs }} />
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: spacing.xxs,
        }}
      >
        <Card>
          <Typography variant="bodyBold">Amount deviation tolerance</Typography>
          <Typography variant="bodySmallest">
            The values below allow for orders to be treated as successful if the
            received amount deviates within the bounds.
          </Typography>
          <div css={{ marginTop: spacing.xxs }} />
          <Input
            label="Lower bound"
            type="number"
            endAdornment="%"
            value={fields.config.amountToleranceLowerBound.toString()}
            onChange={(e) => {
              setters.config.amountToleranceLowerBound.set(
                parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : 0
              );
            }}
            error={!!errors.config.amountToleranceLowerBound}
            helperText={errors.config.amountToleranceLowerBound}
          />
          <Input
            label="Upper bound"
            type="number"
            endAdornment="%"
            value={fields.config.amountToleranceUpperBound.toString()}
            onChange={(e) => {
              setters.config.amountToleranceUpperBound.set(
                parseFloat(e.target.value) >= 0 ? parseFloat(e.target.value) : 0
              );
            }}
            error={!!errors.config.amountToleranceUpperBound}
            helperText={errors.config.amountToleranceUpperBound}
          />
        </Card>
        {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
          <Card>
            <Typography variant="bodyBold">Status</Typography>
            <Typography variant="bodySmallest">
              This toggles whether the merchant is allowed to create orders or
              not.
            </Typography>
            <div css={{ marginTop: spacing.xxs }} />
            <ToggleButtonGroup
              toggleOptions={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Blocked', value: 'BLOCKED' },
              ]}
              selected={fields.status}
              onChange={(value) => {
                setters.status.set(value as Status);
              }}
            />
          </Card>
        )}
        <Card>
          <Typography variant="bodyBold">Order cancellation</Typography>
          <Typography variant="bodySmallest">
            This toggles whether orders should be cancellable or not, and if so
            when they should be cancelled.
          </Typography>
          <div
            css={{
              paddingTop: spacing.xxs,
              paddingRight: spacing.s,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              alignItems: 'center',
            }}
          >
            <Typography variant="body">Cancel after:</Typography>
            <div>
              <Input
                label="Minutes"
                type="number"
                value={fields.config.minutesToCancelOrderAfter.toString()}
                onChange={(e) => {
                  if (e.target.value) {
                    const value = Number(e.target.value);
                    if (value < 0) return;
                    setters.config.minutesToCancelOrderAfter.set(
                      parseInt(e.target.value)
                    );
                  } else {
                    setters.config.minutesToCancelOrderAfter.set(0);
                  }
                }}
                error={!!errors.config.minutesToCancelOrderAfter}
                helperText={errors.config.minutesToCancelOrderAfter}
              />
              <div css={{ textAlign: 'right' }}>
                <Typography variant="bodySmallest">
                  If 0, orders won&apos;t be cancelled.
                </Typography>
              </div>
            </div>
          </div>
          <div
            css={{
              paddingTop: spacing.xxs,
              paddingRight: spacing.s,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body">Cancel orders on error</Typography>
            <Switch
              checked={fields.config.cancelOrderOnError}
              onChange={(checked) => {
                setters.config.cancelOrderOnError.set(checked);
              }}
            />
          </div>
          <div css={{ paddingTop: spacing.xs }}>
            <Typography variant="bodySmall">
              {getCancellationComment(
                fields.config.minutesToCancelOrderAfter,
                fields.config.cancelOrderOnError
              )}
            </Typography>
          </div>
        </Card>
        <Card>
          <Typography variant="bodyBold">Order callbacks</Typography>
          <div
            css={{
              paddingTop: spacing.xxs,
              paddingRight: spacing.s,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body">Retry order callbacks</Typography>
            <Switch
              checked={fields.config.retryOrderCallbacks}
              onChange={(checked) => {
                setters.config.retryOrderCallbacks.set(checked);
              }}
            />
          </div>
        </Card>
        {canEditCorporateWallet && (
          <Card>
            <Typography variant="bodyBold">
              Require Corporate Wallet Recipient Duality
            </Typography>
            <Typography variant="bodySmallest">
              This toggles whether wallet recipients need to be approved by
              someone else before they can be used.
            </Typography>
            <div
              css={{
                paddingTop: spacing.xxs,
                paddingRight: spacing.s,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body">Require Duality</Typography>
              <Switch
                checked={fields.config.useWalletRecipientDuality}
                onChange={(checked) => {
                  setters.config.useWalletRecipientDuality.set(checked);
                }}
                disabled={isRecipientDualityDisabled}
              />
            </div>
            {isRecipientDualityDisabled && (
              <Typography variant="bodySmall" color={palette.danger.default}>
                To disable this, contact Quickbit Support.
              </Typography>
            )}
          </Card>
        )}
        <UtilityButton
          text="Save merchant"
          loading={loading || fetchConfigLoading}
          disabled={hasErrors || !edited}
          onClick={update}
        />
      </div>
      {message && (
        <Snackbar
          message={message.message}
          variant={message.type}
          open={true}
          onClose={() => {
            setMessage(undefined);
          }}
        />
      )}
    </div>
  );
};
